import {Component, Input, OnInit} from '@angular/core';
import {Producto} from '../../../../_models/producto';
import {ProductoMontoInversionService} from '../../../../_services/producto-monto-inversion.service';
import {NavService} from '../../../../_services/_nav.service';
import {ConfirmationService} from 'primeng/api';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ProductoMontoInversion} from '../../../../_models/producto-monto-inversion';
import {firstValueFrom, from} from 'rxjs';
import {CustomValidators} from '../../../../_helpers/formularios-reactivos/custom.validators';
import {AppSettingService} from '../../../../_services/app-setting.service';
import {NgxCurrencyConfig} from 'ngx-currency/lib/ngx-currency.config';

@Component({
  selector: 'app-producto-montos',
  templateUrl: './producto-montos.component.html',
  styleUrl: './producto-montos.component.css'
})
export class ProductoMontosComponent implements OnInit {
  @Input() producto: Producto;
  @Input() isDetail: boolean = false;
  productoMontosInversion: ProductoMontoInversion[] = [];
  loading: boolean = true;
  cols: any = [];
  rows: number = 10;
  productoMontoInversionSelected: ProductoMontoInversion;
  form: FormGroup;
  submitted: boolean = false;
  saving: boolean = false;

  constructor(
    private productoMontoInversionService: ProductoMontoInversionService,
    private navServicio: NavService,
    private confirmationService: ConfirmationService,
    private fb: FormBuilder,
    private appSettingService: AppSettingService,
  ) {}

  ngOnInit(): void {
    this.cols = [
      { field: 'rangoInicial', header: this.navServicio.buscarMensaje('PRODUCTO.LBL_INICIO_RANGO_MONTO'), sort: false },
      { field: 'rangoFinal', header: this.navServicio.buscarMensaje('PRODUCTO.LBL_FIN_RANGO_MONTO'), sort: false },
    ];

    if(!this.isDetail) {
      this.cols.push({ field: 'acciones', header: this.navServicio.buscarMensaje('LBL_ACCIONES'), sort: false });
    }

    from(this.loadProductoMontos()).subscribe({
      next: () => {
        this.loadForm();
        this.loading = false;
      },
    });
  }

  loadForm(): void {
    this.form = this.fb.group({
      idProducto: [ this.producto.idProducto ],
      idProductoMontoInversion: [ this.productoMontoInversionSelected ? this.productoMontoInversionSelected.idProductoMontoInversion : null ],
      rangoInicial: [ this.productoMontoInversionSelected ? this.productoMontoInversionSelected.rangoInicial : null, [Validators.required] ],
      rangoFinal: [ this.productoMontoInversionSelected ? this.productoMontoInversionSelected.rangoFinal : null, [Validators.required] ],
    }, {
      validators: [
        CustomValidators.rangeSequence({ startControlName: 'rangoInicial', endControlName: 'rangoFinal' }),
        CustomValidators.rangeOverlap<ProductoMontoInversion>({
          startControlName: 'rangoInicial',
          endControlName: 'rangoFinal',
          idControlName: 'idProductoMontoInversion',
          ranges: this.productoMontosInversion,
        }),
      ],
    });
  }

  get f() {
    return this.form.controls;
  }

  clearForm(): void {
    this.productoMontoInversionSelected = null;
    this.submitted = false;
    this.loadForm();
  }

  onEdit(row: ProductoMontoInversion): void {
    this.productoMontoInversionSelected = row;
    this.submitted = false;
    this.loadForm();
  }

  onCancel(): void {
    if(this.form.dirty) {
      this.confirmationService.confirm({
        message: this.navServicio.buscarMensaje('MSG_CANCELARCAMBIOS'),
        header: this.navServicio.buscarMensaje('LBL_CONFIRMACION'),
        icon: 'fa fa-question-circle',
        key: 'producto-montos-inversion',
        accept: () => this.clearForm(),
      });
    } else {
      this.clearForm();
    }
  }

  onDelete(row: ProductoMontoInversion): void {
    this.confirmationService.confirm({
      message: this.navServicio.buscarMensaje('MSG_INHABILITAR'),
      header: this.navServicio.buscarMensaje('LBL_CONFIRMACION'),
      icon: 'fa fa-question-circle',
      key: 'producto-montos-inversion',
      accept: () => {
        this.saving = true;
        row.indicadorHabilitado = false;
        this.montoInversionUpdate(row);
      },
    });
  }

  onSubmit(): void {
    this.submitted = true;
    if(this.form.invalid) return;

    this.saving = true;
    const data: ProductoMontoInversion = this.form.value;
    if(data.idProductoMontoInversion) {
      this.montoInversionUpdate(data);
    } else {
      this.productoMontoInversionService.create(data).subscribe({
        next: async (response: ProductoMontoInversion) => {
          await this.loadProductoMontos();
          this.navServicio.setMesage(1);
          this.clearForm();
          this.saving = false;
        },
        error: error => {
          this.navServicio.setMesage(3);
          this.saving = false;
        }
      });
    }
  }

  montoInversionUpdate(data: ProductoMontoInversion): void {
    this.productoMontoInversionService.update(data).subscribe({
      next: async (response: ProductoMontoInversion) => {
        await this.loadProductoMontos();
        this.navServicio.setMesage(2);
        this.clearForm();
        this.saving = false;
      },
      error: error => {
        this.navServicio.setMesage(3);
        this.saving = false;
      }
    });
  }

  async loadProductoMontos(): Promise<void> {
    this.productoMontosInversion = await firstValueFrom(this.productoMontoInversionService.getEnabledByIdProducto(this.producto.idProducto));
  }

  get currencyMaskOptions(): Partial<NgxCurrencyConfig> {
    return {
      prefix: '$ ',
      align: 'left',
      thousands: this.appSettingService.separadorMiles,
      allowNegative: false,
      precision : 0,
      nullable: true,
    };
  }
}
